import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { goalAPI, taskAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { cn, formatDate } from '../lib/utils';
import {
    Plus,
    Target,
    Calendar as CalendarIcon,
    Trash2,
    Edit2,
    CheckCircle2,
    Loader2,
    TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function GoalsPage() {
    const { currentWorkspace, tasks } = useWorkspace();
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);

    useEffect(() => {
        if (currentWorkspace) {
            fetchGoals();
        }
    }, [currentWorkspace]);

    const fetchGoals = async () => {
        setLoading(true);
        try {
            const response = await goalAPI.getByWorkspace(currentWorkspace.id);
            setGoals(response.data);
        } catch (error) {
            console.error('Failed to fetch goals:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGoal = () => {
        setSelectedGoal(null);
        setIsModalOpen(true);
    };

    const handleEditGoal = (goal) => {
        setSelectedGoal(goal);
        setIsModalOpen(true);
    };

    const handleDeleteGoal = async (goalId) => {
        if (!window.confirm('Are you sure you want to delete this goal?')) return;
        try {
            await goalAPI.delete(goalId);
            toast.success('Goal deleted');
            fetchGoals();
        } catch (error) {
            toast.error('Failed to delete goal');
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedGoal(null);
        fetchGoals();
    };

    if (!currentWorkspace) {
        return (
            <Layout title="Goals">
                <div className="empty-state">
                    <p className="text-brand-text-muted">Please select a workspace first</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Goals" subtitle="Track your team's progress towards objectives">
            <div className="space-y-6" data-testid="goals-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-brand-text-secondary">
                            {goals.length} {goals.length === 1 ? 'goal' : 'goals'} in this workspace
                        </p>
                    </div>
                    <Button 
                        onClick={handleCreateGoal}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        data-testid="create-goal-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        New Goal
                    </Button>
                </div>

                {/* Goals Grid */}
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                    </div>
                ) : goals.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {goals.map((goal) => (
                            <GoalCard 
                                key={goal.id}
                                goal={goal}
                                onEdit={() => handleEditGoal(goal)}
                                onDelete={() => handleDeleteGoal(goal.id)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="w-16 h-16 bg-brand-accent-soft rounded-full flex items-center justify-center mb-4">
                            <Target className="w-8 h-8 text-brand-primary" strokeWidth={1.5} />
                        </div>
                        <h2 className="empty-state-title">Set your first goal</h2>
                        <p className="empty-state-description mb-6">
                            Goals help you track progress and keep your team aligned.
                        </p>
                        <Button 
                            onClick={handleCreateGoal}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                        >
                            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Create Goal
                        </Button>
                    </div>
                )}
            </div>

            {/* Goal Modal */}
            <GoalModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                goal={selectedGoal}
                workspaceId={currentWorkspace?.id}
                tasks={tasks}
            />
        </Layout>
    );
}

// Goal Card Component
const GoalCard = ({ goal, onEdit, onDelete }) => {
    const progressColor = goal.progress_percentage >= 100 
        ? '#7ED957' 
        : goal.progress_percentage >= 50 
            ? '#A7E3C8' 
            : '#6EC1E4';

    return (
        <Card className="border-brand-border shadow-card group" data-testid={`goal-card-${goal.id}`}>
            <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <div className={cn(
                            'w-10 h-10 rounded-lg flex items-center justify-center',
                            goal.progress_percentage >= 100 ? 'bg-brand-success/10' : 'bg-brand-accent-soft'
                        )}>
                            {goal.progress_percentage >= 100 ? (
                                <CheckCircle2 className="w-5 h-5 text-brand-success" strokeWidth={1.5} />
                            ) : (
                                <Target className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                            )}
                        </div>
                        <div>
                            <CardTitle className="text-base font-semibold text-brand-text-primary">
                                {goal.name}
                            </CardTitle>
                            {goal.due_date && (
                                <p className="text-xs text-brand-text-muted flex items-center gap-1 mt-0.5">
                                    <CalendarIcon className="w-3 h-3" strokeWidth={1.5} />
                                    Due {formatDate(goal.due_date)}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
                            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-brand-error hover:bg-brand-error/10"
                            onClick={onDelete}
                        >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {goal.description && (
                    <p className="text-sm text-brand-text-secondary mb-4 line-clamp-2">
                        {goal.description}
                    </p>
                )}
                
                {/* Progress */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-brand-text-secondary">Progress</span>
                        <span className="font-semibold text-brand-text-primary">
                            {goal.progress_percentage.toFixed(0)}%
                        </span>
                    </div>
                    <Progress 
                        value={goal.progress_percentage} 
                        className="h-2"
                        style={{ '--progress-color': progressColor }}
                    />
                    <div className="flex items-center justify-between text-xs text-brand-text-muted">
                        <span>{goal.current_value} / {goal.target_value} {goal.unit}</span>
                        {goal.linked_task_ids?.length > 0 && (
                            <span>{goal.linked_task_ids.length} linked tasks</span>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Goal Modal Component
const GoalModal = ({ isOpen, onClose, goal, workspaceId, tasks }) => {
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [targetValue, setTargetValue] = useState('');
    const [unit, setUnit] = useState('tasks');
    const [dueDate, setDueDate] = useState(null);
    const [linkedTaskIds, setLinkedTaskIds] = useState([]);

    useEffect(() => {
        if (goal) {
            setName(goal.name || '');
            setDescription(goal.description || '');
            setTargetValue(String(goal.target_value) || '');
            setUnit(goal.unit || 'tasks');
            setDueDate(goal.due_date ? new Date(goal.due_date) : null);
            setLinkedTaskIds(goal.linked_task_ids || []);
        } else {
            setName('');
            setDescription('');
            setTargetValue('');
            setUnit('tasks');
            setDueDate(null);
            setLinkedTaskIds([]);
        }
    }, [goal, isOpen]);

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error('Goal name is required');
            return;
        }
        if (!targetValue || parseFloat(targetValue) <= 0) {
            toast.error('Please enter a valid target value');
            return;
        }

        setSaving(true);
        try {
            const goalData = {
                name,
                description,
                target_value: parseFloat(targetValue),
                unit,
                due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
                linked_task_ids: linkedTaskIds,
                workspace_id: workspaceId,
            };

            if (goal) {
                await goalAPI.update(goal.id, goalData);
                toast.success('Goal updated');
            } else {
                await goalAPI.create(goalData);
                toast.success('Goal created');
            }
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save goal');
        } finally {
            setSaving(false);
        }
    };

    const toggleTaskLink = (taskId) => {
        if (linkedTaskIds.includes(taskId)) {
            setLinkedTaskIds(linkedTaskIds.filter(id => id !== taskId));
        } else {
            setLinkedTaskIds([...linkedTaskIds, taskId]);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-lg" data-testid="goal-modal">
                <DialogHeader>
                    <DialogTitle className="font-heading">
                        {goal ? 'Edit Goal' : 'Create Goal'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    <div>
                        <Label>Goal Name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Complete Q1 deliverables"
                            className="mt-1"
                            data-testid="goal-name-input"
                        />
                    </div>

                    <div>
                        <Label>Description</Label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What is this goal about?"
                            className="mt-1"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Target Value</Label>
                            <Input
                                type="number"
                                value={targetValue}
                                onChange={(e) => setTargetValue(e.target.value)}
                                placeholder="e.g., 10"
                                className="mt-1"
                                data-testid="goal-target-input"
                            />
                        </div>
                        <div>
                            <Label>Unit</Label>
                            <Input
                                value={unit}
                                onChange={(e) => setUnit(e.target.value)}
                                placeholder="e.g., tasks, hours, %"
                                className="mt-1"
                            />
                        </div>
                    </div>

                    <div>
                        <Label>Due Date</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full mt-1 justify-start">
                                    <CalendarIcon className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    {dueDate ? format(dueDate, 'MMM d, yyyy') : 'Select date'}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dueDate}
                                    onSelect={setDueDate}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* Link Tasks */}
                    {tasks.length > 0 && (
                        <div>
                            <Label>Link Tasks ({linkedTaskIds.length} selected)</Label>
                            <div className="mt-2 max-h-40 overflow-y-auto border border-brand-border rounded-lg">
                                {tasks.slice(0, 20).map(task => (
                                    <div 
                                        key={task.id}
                                        className={cn(
                                            'flex items-center gap-3 p-2 cursor-pointer hover:bg-brand-accent-soft/50 transition-colors',
                                            linkedTaskIds.includes(task.id) && 'bg-brand-accent-soft'
                                        )}
                                        onClick={() => toggleTaskLink(task.id)}
                                    >
                                        <div className={cn(
                                            'w-4 h-4 rounded border flex items-center justify-center',
                                            linkedTaskIds.includes(task.id) 
                                                ? 'bg-brand-primary border-brand-primary' 
                                                : 'border-brand-border'
                                        )}>
                                            {linkedTaskIds.includes(task.id) && (
                                                <CheckCircle2 className="w-3 h-3 text-white" />
                                            )}
                                        </div>
                                        <span className="text-sm truncate">{task.title}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button 
                        onClick={handleSave}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        disabled={saving}
                        data-testid="goal-save-btn"
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {goal ? 'Save Changes' : 'Create Goal'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
